import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Card, Button, Badge, Modal, FormField, Spinner } from '../components/ui';

const DECISION_COLORS = { pending: 'due_soon', approved: 'valid', refused: 'expired' };
const STATUS_COLORS   = { served: 'due_soon', withdrawn: 'unknown', court_proceedings: 'expired', resolved: 'valid' };
const PET_TYPES = ['dog','cat','small_animal','bird','other'];
const DELIVERY  = ['email','post','hand_delivered'];

export default function RRA() {
  const [dashboard, setDashboard]     = useState(null);
  const [properties, setProperties]   = useState([]);
  const [selected, setSelected]       = useState(null);
  const [tenants, setTenants]         = useState([]);
  const [rentReviews, setRentReviews] = useState([]);
  const [petRequests, setPetRequests] = useState([]);
  const [possessions, setPossessions] = useState([]);
  const [grounds, setGrounds]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState('info_sheet');

  // Modals
  const [showRentModal, setShowRentModal]   = useState(false);
  const [showPetModal, setShowPetModal]     = useState(false);
  const [showPossModal, setShowPossModal]   = useState(false);
  const [saving, setSaving]                 = useState(false);

  // Forms
  const emptyRent = { tenant_id:'', current_rent_gbp:'', last_increase_date:'', next_review_date:'', notes:'' };
  const emptyPet  = { tenant_id:'', pet_type:'dog', pet_description:'', requested_at: new Date().toISOString().split('T')[0], decision:'pending', notes:'' };
  const emptyPoss = { tenant_id:'', ground:'', notice_date: new Date().toISOString().split('T')[0], notes:'' };
  const [rentForm, setRentForm] = useState(emptyRent);
  const [petForm,  setPetForm]  = useState(emptyPet);
  const [possForm, setPossForm] = useState(emptyPoss);

  useEffect(() => {
    Promise.all([
      api.rraDashboard(),
      api.getProperties(),
      api.getPossessionGrounds(),
    ]).then(([dash, props, grnds]) => {
      setDashboard(dash);
      setProperties(props || []);
      setGrounds(grnds || []);
      setLoading(false);
    });
  }, []);

  async function selectProperty(p) {
    setSelected(p);
    const [t, rr, pr, pn] = await Promise.all([
      api.getTenants(p.id),
      api.getRentReviews(p.id),
      api.getPetRequests(p.id),
      api.getPossessionNotices(p.id),
    ]);
    setTenants(t || []);
    setRentReviews(rr || []);
    setPetRequests(pr || []);
    setPossessions(pn || []);
  }

  async function toggleInfoSheet(tenant, sent, method) {
    await api.updateInfoSheet(tenant.id, { info_sheet_sent: sent, info_sheet_method: method });
    const updated = await api.getTenants(selected.id);
    setTenants(updated || []);
    const dash = await api.rraDashboard();
    setDashboard(dash);
  }

  async function saveRentReview() {
    setSaving(true);
    try {
      const payload = { ...rentForm, current_rent_gbp: parseInt(rentForm.current_rent_gbp) || 0 };
      await api.createRentReview(selected.id, payload);
      const updated = await api.getRentReviews(selected.id);
      setRentReviews(updated || []);
      setShowRentModal(false); setRentForm(emptyRent);
    } finally { setSaving(false); }
  }

  async function savePetRequest() {
    setSaving(true);
    try {
      await api.createPetRequest(selected.id, petForm);
      const updated = await api.getPetRequests(selected.id);
      setPetRequests(updated || []);
      setShowPetModal(false); setPetForm(emptyPet);
    } finally { setSaving(false); }
  }

  async function updatePetDecision(id, decision, refusalReason) {
    await api.updatePetRequest(id, { decision, decision_date: new Date().toISOString().split('T')[0], refusal_reason: refusalReason || '' });
    const updated = await api.getPetRequests(selected.id);
    setPetRequests(updated || []);
  }

  async function savePossession() {
    setSaving(true);
    try {
      await api.createPossessionNotice(selected.id, possForm);
      const updated = await api.getPossessionNotices(selected.id);
      setPossessions(updated || []);
      setShowPossModal(false); setPossForm(emptyPoss);
    } finally { setSaving(false); }
  }

  if (loading) return <Spinner />;

  const tabs = [
    { id: 'info_sheet', label: '📋 Info Sheet', count: dashboard?.info_sheets_pending },
    { id: 'rent',       label: '💷 Rent Reviews', count: dashboard?.rent_reviews_due },
    { id: 'pets',       label: '🐾 Pet Requests', count: dashboard?.pet_requests_pending },
    { id: 'possession', label: '🏠 Possession', count: 0 },
  ];

  return (
    <div className="fade-up">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>Renters' Rights <em>Act 2025</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>Compliance tools for the new legislation — in force from 1 May 2026</p>
      </div>

      {/* RRA dashboard summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Info sheets pending', value: dashboard?.info_sheets_pending, color: dashboard?.info_sheets_pending > 0 ? 'var(--danger)' : 'var(--accent)', deadline: '31 May 2026' },
          { label: 'Rent reviews due',    value: dashboard?.rent_reviews_due,    color: dashboard?.rent_reviews_due > 0 ? 'var(--warn)' : 'var(--accent)', deadline: 'Within 60 days' },
          { label: 'Pet requests pending',value: dashboard?.pet_requests_pending,color: dashboard?.pet_requests_pending > 0 ? 'var(--warn)' : 'var(--accent)', deadline: 'Action required' },
          { label: 'Fixed-term tenancies',value: dashboard?.fixed_term_pending,  color: dashboard?.fixed_term_pending > 0 ? 'var(--danger)' : 'var(--accent)', deadline: 'Convert by 1 May 2026' },
        ].map(s => (
          <Card key={s.label} style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 32, fontFamily: 'var(--font-serif)', fontWeight: 300, color: s.color }}>{s.value ?? 0}</p>
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginTop: 4 }}>{s.label}</p>
            <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{s.deadline}</p>
          </Card>
        ))}
      </div>

      {/* Info sheet deadline banner */}
      {dashboard?.info_sheets_pending > 0 && (
        <Card style={{ marginBottom: 24, background: 'var(--danger-lt)', borderColor: 'var(--danger)' }}>
          <p style={{ fontWeight: 600, color: 'var(--danger)', fontSize: 15 }}>
            🚨 {dashboard.info_sheets_pending} tenant{dashboard.info_sheets_pending > 1 ? 's' : ''} haven't received the mandatory RRA Information Sheet
          </p>
          <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
            Deadline: <strong>31 May 2026</strong> · Fine up to £7,000 ·{' '}
            <a href="https://www.gov.uk/government/publications/the-renters-rights-act-information-sheet-2026" target="_blank" rel="noreferrer" style={{ color: 'var(--danger)', fontWeight: 600 }}>
              Download from GOV.UK →
            </a>
          </p>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '260px 1fr' : '1fr', gap: 24 }}>

        {/* Property list */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>Select property</p>
          {properties.map(p => (
            <Card key={p.id} onClick={() => selectProperty(p)} style={{ marginBottom: 8, cursor: 'pointer', borderColor: selected?.id === p.id ? 'var(--accent)' : 'var(--border)' }}>
              <p style={{ fontWeight: 500, fontSize: 14 }}>{p.address_line1}</p>
              <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>{p.city}, {p.postcode}</p>
            </Card>
          ))}
        </div>

        {/* Property RRA detail */}
        {selected && (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>{selected.address_line1}</h2>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
                  background: tab === t.id ? 'var(--accent)' : 'var(--border)',
                  color: tab === t.id ? '#fff' : 'var(--ink)',
                  fontWeight: tab === t.id ? 600 : 400,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {t.label}
                  {t.count > 0 && <span style={{ background: 'var(--danger)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>{t.count}</span>}
                </button>
              ))}
            </div>

            {/* ── INFO SHEET TAB ── */}
            {tab === 'info_sheet' && (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 16 }}>Information Sheet Tracker</h3>
                    <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Must be sent to all tenants by 31 May 2026 · Fine up to £7,000</p>
                  </div>
                  <a href="https://www.gov.uk/government/publications/the-renters-rights-act-information-sheet-2026" target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost">Download sheet →</Button>
                  </a>
                </div>
                {!tenants.length ? <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No tenants added for this property</p> : (
                  tenants.map(t => (
                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <p style={{ fontWeight: 500, fontSize: 14 }}>{t.full_name}</p>
                        {t.info_sheet_sent && <p style={{ fontSize: 12, color: 'var(--accent)' }}>✅ Sent {t.info_sheet_method ? `via ${t.info_sheet_method.replace('_', ' ')}` : ''} {t.info_sheet_sent_at ? `on ${t.info_sheet_sent_at.split('T')[0]}` : ''}</p>}
                        {!t.info_sheet_sent && <p style={{ fontSize: 12, color: 'var(--danger)' }}>❌ Not yet sent</p>}
                      </div>
                      {!t.info_sheet_sent ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {DELIVERY.map(m => (
                            <button key={m} onClick={() => toggleInfoSheet(t, true, m)} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--accent)', background: 'var(--accent-lt)', color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                              Sent via {m.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <button onClick={() => toggleInfoSheet(t, false, '')} style={{ fontSize: 12, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer' }}>Undo</button>
                      )}
                    </div>
                  ))
                )}
              </Card>
            )}

            {/* ── RENT REVIEWS TAB ── */}
            {tab === 'rent' && (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 16 }}>Section 13 Rent Reviews</h3>
                    <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Rent can only increase once per year · 2 months' notice required</p>
                  </div>
                  <Button size="sm" onClick={() => { setRentForm({ ...emptyRent }); setShowRentModal(true); }}>+ Add</Button>
                </div>
                {!rentReviews.length ? <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No rent reviews recorded</p> : (
                  rentReviews.map(rv => (
                    <div key={rv.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <p style={{ fontWeight: 500, fontSize: 14 }}>£{(rv.current_rent_gbp / 100).toFixed(2)}/month</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>Last increase: {rv.last_increase_date || '—'} · Next review: {rv.next_review_date || '—'}</p>
                        </div>
                        <Badge status={rv.s13_notice_served ? 'valid' : 'due_soon'} label={rv.s13_notice_served ? 'S13 Served' : 'S13 Pending'} />
                      </div>
                      {rv.s13_notice_date && <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>Notice served: {rv.s13_notice_date} · Effective: {rv.s13_effective_date}</p>}
                    </div>
                  ))
                )}
              </Card>
            )}

            {/* ── PET REQUESTS TAB ── */}
            {tab === 'pets' && (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 16 }}>Pet Requests</h3>
                    <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Blanket pet bans are illegal · Refusals must have a reasonable documented reason</p>
                  </div>
                  <Button size="sm" onClick={() => { setPetForm(emptyPet); setShowPetModal(true); }}>+ Add</Button>
                </div>
                {!petRequests.length ? <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No pet requests recorded</p> : (
                  petRequests.map(pr => (
                    <div key={pr.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <p style={{ fontWeight: 500, fontSize: 14, textTransform: 'capitalize' }}>{pr.pet_type} {pr.pet_description ? `— ${pr.pet_description}` : ''}</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>Requested: {pr.requested_at} · Tenant: {tenants.find(t => t.id === pr.tenant_id)?.full_name || pr.tenant_id}</p>
                          {pr.refusal_reason && <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2 }}>Refusal reason: {pr.refusal_reason}</p>}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <Badge status={DECISION_COLORS[pr.decision]} label={pr.decision} />
                          {pr.decision === 'pending' && (
                            <>
                              <button onClick={() => updatePetDecision(pr.id, 'approved', '')} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}>Approve</button>
                              <button onClick={() => { const reason = prompt('Reason for refusal (required):'); if (reason) updatePetDecision(pr.id, 'refused', reason); }} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, background: 'var(--danger)', color: '#fff', border: 'none', cursor: 'pointer' }}>Refuse</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            )}

            {/* ── POSSESSION TAB ── */}
            {tab === 'possession' && (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 16 }}>Possession Notices</h3>
                    <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Section 21 abolished — all possession must use defined grounds</p>
                  </div>
                  <Button size="sm" onClick={() => { setPossForm(emptyPoss); setShowPossModal(true); }}>+ Add notice</Button>
                </div>
                {!possessions.length ? <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No possession notices recorded</p> : (
                  possessions.map(pn => (
                    <div key={pn.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <p style={{ fontWeight: 500, fontSize: 14 }}>{pn.ground_label || pn.ground}</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>Notice: {pn.notice_date} · Effective: {pn.effective_date || '—'} · {pn.notice_period_weeks} weeks notice</p>
                        </div>
                        <Badge status={STATUS_COLORS[pn.status]} label={pn.status?.replace('_', ' ')} />
                      </div>
                    </div>
                  ))
                )}
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Rent Review Modal */}
      <Modal open={showRentModal} onClose={() => setShowRentModal(false)} title="Add Rent Review">
        <FormField label="Tenant">
          <select value={rentForm.tenant_id} onChange={e => setRentForm(f => ({ ...f, tenant_id: e.target.value }))}>
            <option value="">Select tenant...</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>
        </FormField>
        <FormField label="Current rent (£/month)">
          <input type="number" value={rentForm.current_rent_gbp} onChange={e => setRentForm(f => ({ ...f, current_rent_gbp: e.target.value }))} placeholder="e.g. 1200" />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label="Last increase date">
            <input type="date" value={rentForm.last_increase_date} onChange={e => {
              const d = e.target.value;
              const next = d ? new Date(new Date(d).setFullYear(new Date(d).getFullYear() + 1)).toISOString().split('T')[0] : '';
              setRentForm(f => ({ ...f, last_increase_date: d, next_review_date: next }));
            }} />
          </FormField>
          <FormField label="Next review due">
            <input type="date" value={rentForm.next_review_date} onChange={e => setRentForm(f => ({ ...f, next_review_date: e.target.value }))} />
            <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>Auto-set to 12 months after last increase</p>
          </FormField>
        </div>
        <FormField label="Notes (optional)">
          <textarea rows={2} value={rentForm.notes} onChange={e => setRentForm(f => ({ ...f, notes: e.target.value }))} />
        </FormField>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={() => setShowRentModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={saveRentReview}>Save</Button>
        </div>
      </Modal>

      {/* Pet Request Modal */}
      <Modal open={showPetModal} onClose={() => setShowPetModal(false)} title="Record Pet Request">
        <FormField label="Tenant">
          <select value={petForm.tenant_id} onChange={e => setPetForm(f => ({ ...f, tenant_id: e.target.value }))}>
            <option value="">Select tenant...</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label="Pet type">
            <select value={petForm.pet_type} onChange={e => setPetForm(f => ({ ...f, pet_type: e.target.value }))}>
              {PET_TYPES.map(p => <option key={p} value={p}>{p.replace('_', ' ')}</option>)}
            </select>
          </FormField>
          <FormField label="Requested on">
            <input type="date" value={petForm.requested_at} onChange={e => setPetForm(f => ({ ...f, requested_at: e.target.value }))} />
          </FormField>
        </div>
        <FormField label="Description (optional)">
          <input value={petForm.pet_description} onChange={e => setPetForm(f => ({ ...f, pet_description: e.target.value }))} placeholder="e.g. Golden retriever, 3 years old" />
        </FormField>
        <FormField label="Notes">
          <textarea rows={2} value={petForm.notes} onChange={e => setPetForm(f => ({ ...f, notes: e.target.value }))} />
        </FormField>
        <div style={{ background: 'var(--warn-lt)', border: '1px solid var(--warn)', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
          <p style={{ fontSize: 12, color: 'var(--warn)' }}>⚠️ Under the Renters' Rights Act, you cannot blanket refuse pet requests. Any refusal must have a documented, reasonable reason.</p>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setShowPetModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={savePetRequest}>Save</Button>
        </div>
      </Modal>

      {/* Possession Notice Modal */}
      <Modal open={showPossModal} onClose={() => setShowPossModal(false)} title="Serve Possession Notice">
        <div style={{ background: 'var(--warn-lt)', border: '1px solid var(--warn)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: 'var(--warn)' }}>⚠️ Section 21 is abolished. You must use a valid possession ground under the Renters' Rights Act 2025.</p>
        </div>
        <FormField label="Tenant">
          <select value={possForm.tenant_id} onChange={e => setPossForm(f => ({ ...f, tenant_id: e.target.value }))}>
            <option value="">Select tenant...</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>
        </FormField>
        <FormField label="Possession ground">
          <select value={possForm.ground} onChange={e => {
            const g = grounds.find(x => x.key === e.target.value);
            setPossForm(f => ({ ...f, ground: e.target.value, notice_period_weeks: g?.notice_weeks || 2 }));
          }}>
            <option value="">Select ground...</option>
            {grounds.map(g => (
              <option key={g.key} value={g.key}>{g.label} ({g.notice_weeks === 0 ? 'immediate' : `${g.notice_weeks} weeks notice`})</option>
            ))}
          </select>
          {possForm.ground && (
            <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
              {grounds.find(g => g.key === possForm.ground)?.description}
            </p>
          )}
        </FormField>
        <FormField label="Notice date">
          <input type="date" value={possForm.notice_date} onChange={e => setPossForm(f => ({ ...f, notice_date: e.target.value }))} />
        </FormField>
        <FormField label="Notes / evidence">
          <textarea rows={3} value={possForm.notes} onChange={e => setPossForm(f => ({ ...f, notes: e.target.value }))} placeholder="Document your evidence and reasons here..." />
        </FormField>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setShowPossModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={savePossession}>Record notice</Button>
        </div>
      </Modal>
    </div>
  );
}